import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Button } from "@/atoms/button";
import { Crescent } from "@/atoms/crescent";
import { EmptyState } from "@/components/empty-state";

/**
 * The old checkout link, forwarded to the new one.
 *
 * The wait is the crescent; after four seconds a secondary button appears, because a redirect
 * that has not happened by then usually needs a tap on a phone that blocked it. The API call and
 * its 404 branch are the ones this page has always had.
 */

type State = "loading" | "redirecting" | "not-found" | "error";

export function Checkoutv1RedirectPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<State>("loading");
  const [showFallback, setShowFallback] = useState(false);

  const fetchAndRedirect = useCallback(async () => {
    if (!id) {
      setState("not-found");
      return;
    }
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/automatedSaleTemplates/generateStripeLink/${id}`,
      );
      const { checkoutLink } = response.data as { checkoutLink: string };
      setState("redirecting");
      window.location.replace(checkoutLink);
    } catch (err) {
      setState(
        axios.isAxiosError(err) && err.response?.status === 404
          ? "not-found"
          : "error",
      );
    }
  }, [id]);

  useEffect(() => {
    void fetchAndRedirect();
    const timer = window.setTimeout(() => setShowFallback(true), 4000);
    return () => window.clearTimeout(timer);
  }, [fetchAndRedirect]);

  return (
    <div className="bg-surface-paper px-gk-4 flex min-h-screen items-center justify-center">
      {state === "not-found" ? (
        <div className="w-full max-w-[520px]">
          <EmptyState
            heading={t("gymkeePay.common.notFoundHeading")}
            line={t("gymkeePay.common.notFoundLine")}
            inset={false}
          />
        </div>
      ) : state === "error" ? (
        <div className="w-full max-w-[520px]">
          <EmptyState
            heading={t("gymkeePay.checkout.loadFailedHeading")}
            inset={false}
            action={
              <Button
                variant="secondary"
                onClick={() => void fetchAndRedirect()}
              >
                {t("gymkeePay.common.tryAgain")}
              </Button>
            }
          />
        </div>
      ) : (
        <div className="gap-gk-6 flex flex-col items-center">
          <Crescent
            size={48}
            delay={0}
            label={t("gymkeePay.common.openingPaymentPage")}
          />
          {showFallback && (
            <Button variant="secondary" onClick={() => void fetchAndRedirect()}>
              {t("gymkeePay.common.openPaymentPage")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
